import { useState, useEffect, MouseEvent, useRef } from "react"
import {
  IContextMenuEvent,
  IContextMenuPoints,
  IUseContextMenu,
  IUseContextMenuReturn
} from "./model"

const useContextMenu = ({
  id,
  menuHeight,
  menuWidth
}: IUseContextMenu): IUseContextMenuReturn => {
  const menuRef = useRef<HTMLDivElement>(null)
  const [clicked, setClicked] = useState(false)
  const [points, setPoints] = useState<IContextMenuPoints>({
    x: 0,
    y: 0,
  })
  const [preventClose, setPreventClose] = useState(false)

  useEffect(() => {
    const handleClick = (e: Event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !preventClose
      ) {
        setClicked(false)
      }
    }

    const handleContextMenu = (e: CustomEvent<IContextMenuEvent>) => {
      if (e.detail.id !== id) {
        if (!preventClose) setClicked(false)
      }
    }

    document.addEventListener("click", handleClick)
    document.addEventListener("contextmenu:open", handleContextMenu as EventListener)

    return () => {
      document.removeEventListener("click", handleClick)
      document.removeEventListener("contextmenu:open", handleContextMenu as EventListener)
    }
  }, [id, preventClose])

  const calculateAdjustedPosition = (clientX: number, clientY: number) => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const menuWidthValue = menuWidth
    const menuHeightValue = menuHeight

    let adjustedX = clientX
    let adjustedY = clientY

    if (clientX + menuWidthValue > viewportWidth) {
      adjustedX = clientX - menuWidthValue
    }

    if (adjustedX < 0) {
      adjustedX = 10
    }

    if (clientY + menuHeightValue > viewportHeight) {
      adjustedY = clientY - menuHeightValue
    }

    if (adjustedY < 0) {
      adjustedY = 10
    }

    adjustedX = Math.max(10, Math.min(adjustedX, viewportWidth - menuWidthValue - 10))
    adjustedY = Math.max(10, Math.min(adjustedY, viewportHeight - menuHeightValue - 10))

    return {
      x: adjustedX,
      y: adjustedY,
    }
  }

  const show = (e: MouseEvent) => {
    e.preventDefault()

    const event = new CustomEvent<IContextMenuEvent>("contextmenu:open",
      { detail: { id } }
    )
    document.dispatchEvent(event)

    setClicked(true)

    const adjustedPosition = calculateAdjustedPosition(e.clientX, e.clientY)

    setPoints(adjustedPosition)
  }

  const hide = () => {
    if (!preventClose) setClicked(false)
  }

  const togglePreventClose = (value: boolean) => {
    setPreventClose(value)
  }

  return {
    clicked,
    points,
    menuRef,
    setClicked,
    setPoints,
    show,
    hide,
    togglePreventClose
  }
}

export default useContextMenu
